const LOCK_KEY = 'fc.inviteGuest.lock';
const LOCK_MS = 12_000;

export function shouldAutoRegisterInviteGuest(input: {
  agentCode: string;
  authenticated: boolean;
  preview: boolean;
  referralCode: string;
}) {
  if (input.preview || input.authenticated) return false;
  return Boolean(input.agentCode.trim() || input.referralCode.trim());
}

let inflight: Promise<boolean> | null = null;

/** 测试隔离用。运行中的邀请落地只应有一次游客注册。 */
export function resetInviteGuestFlight() {
  inflight = null;
}

function lockHeld(storage: Storage, now: number) {
  const raw = storage.getItem(LOCK_KEY);
  if (!raw) return false;
  const until = Number(raw.split(':')[0]);
  return Number.isFinite(until) && until > now;
}

/**
 * 同一浏览器同时打开多条邀请链接时，只注册一个游客。
 * 返回 true 表示本地已经有会话，或本次注册成功。
 */
export function registerInviteGuest(deps: {
  authenticated: () => boolean;
  login: () => Promise<boolean>;
  now?: () => number;
  restore: () => boolean;
  storage?: Storage;
  wait?: (ms: number) => Promise<void>;
}) {
  if (deps.authenticated()) return Promise.resolve(true);
  if (inflight) return inflight;
  inflight = runInviteGuest(deps).finally(() => {
    inflight = null;
  });
  return inflight;
}

async function runInviteGuest(deps: {
  authenticated: () => boolean;
  login: () => Promise<boolean>;
  now?: () => number;
  restore: () => boolean;
  storage?: Storage;
  wait?: (ms: number) => Promise<void>;
}) {
  const storage = deps.storage ?? localStorage;
  const now = deps.now ?? Date.now;
  const wait = deps.wait ?? ((ms: number) => new Promise((resolve) => setTimeout(resolve, ms)));
  const started = now();
  while (lockHeld(storage, now())) {
    if (deps.restore() || deps.authenticated()) return true;
    if (now() - started > LOCK_MS) break;
    await wait(100);
  }
  if (deps.restore() || deps.authenticated()) return true;
  const token = `${now() + LOCK_MS}:${Math.random().toString(36).slice(2)}`;
  try {
    storage.setItem(LOCK_KEY, token);
  } catch {
    // 无痕模式写不进锁时仍尝试注册，避免邀请落地停在未登录。
  }
  try {
    if (deps.authenticated()) return true;
    return await deps.login();
  } finally {
    try {
      if (storage.getItem(LOCK_KEY) === token) storage.removeItem(LOCK_KEY);
    } catch {
      // ignore
    }
  }
}
